import React, { useMemo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { LinearProgress } from '@material-ui/core';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';

import { t } from '~/helpers';
import api from '~/services/api';

import { Header, Table } from './components';
import * as S from './styles';

const RELATORIO_URL = 'pitstop/relatorio/controleabastecimento';

const FIELD_LIST =
  'dataencostecontroleabastecimento,horaencostecontroleabastecimento,' +
  'idveiculo,trematendimento,idbicoabastecimento,' +
  'volumemovimentacaoabastecimento,datahorainicialmovimentacaoabastecimento,' +
  'datahorafinalmovimentacaoabastecimento,duracaoabastecimentodieselcontroleabastecimento,' +
  'vazaoabastecimentodieselcontroleabastecimento,datainicioatendimentoareia,' +
  'datafimatendimentoareia,duracaoareia,agua,' +
  'limpeza,qtdtampascontroleabastecimento,direitocontroleabastecimento,' +
  'esquerdocontroleabastecimento,dataliberacaocontroleabastecimento,horaliberacaocontroleabastecimento,' +
  'pitstopcontroleabastecimento,nomeusuarioabastecimento,codigoatendimento';

function RelPitStopControleAbastecimento() {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [textoPesquisa, setTextoPesquisa] = useState('');
  const [lastReportParams, setLastReportParams] = useState(null);

  const header = useMemo(() => {
    const agora = moment();

    return {
      datainicio: agora
        .clone()
        //   .subtract(7, 'day')
        .format('DD/MM/YYYY 00:00:00'),
      datafim: agora.clone().format('DD/MM/YYYY 23:59:59'),
      idposto: '',
      codigonomeposto: '',
    };
  }, []);

  const validaCamposObrigatorios = useCallback((formData) => {
    if (formData.datainicio && formData.datafim) {
      if (formData.datainicio.length != 19 || formData.datafim.length != 19) {
        toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
        return false;
      }
    } else {
      toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
      return false;
    }

    if (!formData.idposto || Number(formData.idposto) <= 0) {
      toast.error(t('s.operacao.descarganf.postoobrigatorio'));
      return false;
    }

    return true;
  }, []);

  const getFiltroPesquisa = useCallback((formData) => {
    let filtro =
      `FILTRO - ${t('s.geral.opcoes.datahorainicial')}: ${
        formData.datainicio
      } | ` + `${t('s.geral.opcoes.datahorafinal')}: ${formData.datafim}`;

    if (formData.codigonomeposto) {
      filtro += ` | ${t('s.geral.postocomboio')}: ${formData.codigonomeposto}`;
    }

    return filtro;
  }, []);

  const getParams = useCallback(
    (formData) => ({
      dataInicio: moment(formData.datainicio, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      dataFim: moment(formData.datafim, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      idposto: Number(formData.idposto),
    }),
    []
  );

  const handleSubmitHeader = useCallback(
    async (formData) => {
      if (!validaCamposObrigatorios(formData)) {
        return;
      }

      try {
        setLoading(true);

        const params = getParams(formData);
        const filtro = getFiltroPesquisa(formData);

        setLastReportParams(params);
        setTextoPesquisa(filtro);

        const { data } = await api.get(RELATORIO_URL, {
          params,
          paramsSerializer: (param) => qs.stringify(param),
        });

        setDataTable(data || []);
      } catch (err) {
        if (isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [getFiltroPesquisa, getParams, validaCamposObrigatorios]
  );

  const handleExportData = useCallback(
    async (formato) => {
      try {
        setLoading(true);

        if (!lastReportParams) {
          return;
        }

        const tituloRelatorio = t('s.relatorio.pitstopcontroleabastecimento');

        const response = await api.post(
          `${RELATORIO_URL}/export`,
          {
            fieldlist: FIELD_LIST,
            relatorio: {
              custom: 'true',
              formato,
              tituloRelatorio,
              textoPesquisa,
              substituirHeader: JSON.stringify({
                dataencostecontroleabastecimento: {
                  label: t('s.relatorio.pitstop.dataencoste'),
                  align: 'center',
                },
                horaencostecontroleabastecimento: {
                  label: t('s.relatorio.pitstop.horaencoste'),
                  align: 'center',
                },
                idveiculo: {
                  label: t('s.menu.cadastro.veiculo.locomotiva'),
                  align: 'left',
                },
                trematendimento: {
                  label: t('s.cadastro.pitstop.trem'),
                  align: 'left',
                },
                idbicoabastecimento: {
                  label: t('s.menu.configuracao.bico'),
                  align: 'right',
                },
                volumemovimentacaoabastecimento: {
                  label: t('s.global.volume'),
                  align: 'right',
                },
                datahorainicialmovimentacaoabastecimento: {
                  label: t('s.geral.horainicial'),
                  align: 'center',
                },
                datahorafinalmovimentacaoabastecimento: {
                  label: t('s.geral.horafinal'),
                  align: 'center',
                },
                duracaoabastecimentodieselcontroleabastecimento: {
                  label: t('s.geral.duracao'),
                  align: 'center',
                },
                vazaoabastecimentodieselcontroleabastecimento: {
                  label: t('s.geral.vazao'),
                  align: 'right',
                },
                datainicioatendimentoareia: {
                  label: t('s.relatorio.pitstop.horainicioareia'),
                  align: 'center',
                },
                datafimatendimentoareia: {
                  label: t('s.relatorio.pitstop.horafinalareia'),
                  align: 'center',
                },
                duracaoareia: {
                  label: t('s.relatorio.pitstop.duracaoareia'),
                  align: 'center',
                },
                agua: { label: t('s.cadastro.pitstop.agua'), align: 'center' },
                limpeza: {
                  label: t('s.cadastro.pitstop.limpeza'),
                  align: 'center',
                },
                qtdtampascontroleabastecimento: {
                  label: t('s.relatorio.pitstop.qtdtampas'),
                  align: 'center',
                },
                direitocontroleabastecimento: {
                  label: t('s.relatorio.pitstop.direito'),
                  align: 'center',
                },
                esquerdocontroleabastecimento: {
                  label: t('s.relatorio.pitstop.esquerdo'),
                  align: 'center',
                },
                dataliberacaocontroleabastecimento: {
                  label: t('s.relatorio.pitstop.dataliberacao'),
                  align: 'center',
                },
                horaliberacaocontroleabastecimento: {
                  label: t('s.relatorio.pitstop.horaliberacao'),
                  align: 'center',
                },
                pitstopcontroleabastecimento: {
                  label: t('s.menu.operacao.pitstop'),
                  align: 'center',
                },
                nomeusuarioabastecimento: {
                  label: t('s.relatorio.pitstop.abastecedor'),
                  align: 'left',
                },
                codigoatendimento: {
                  label: t('s.cadastro.pitstop.codigoatendimento'),
                  align: 'left',
                },
              }),
            },
          },
          {
            params: lastReportParams,
            paramsSerializer: (param) => qs.stringify(param),
            responseType: 'blob',
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;

        if (formato.toLowerCase() === 'excel') {
          link.download = `${tituloRelatorio}.xls`;
        } else {
          link.target = '_blank';
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        if (Array.isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [lastReportParams, textoPesquisa]
  );

  return (
    <S.Wrapper>
      {loading && <LinearProgress />}
      <Header
        data={header}
        loading={loading}
        handleSubmit={handleSubmitHeader}
        canExport={dataTable.length > 0}
        onExportClick={handleExportData}
      />

      <Table rows={dataTable} />
    </S.Wrapper>
  );
}

export default RelPitStopControleAbastecimento;
